import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Wallet, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { PROJECT_STATUS_COLOR, moneyExact } from "@/lib/constants";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DashboardTab from "@/components/project/DashboardTab";
import TasksTab from "@/components/project/TasksTab";
import BlueprintsTab from "@/components/project/BlueprintsTab";
import ChannelsTab from "@/components/project/ChannelsTab";
import MembersTab from "@/components/project/MembersTab";

export default function ProjectWorkspace() {
  const { id } = useParams();
  const { data: project, isLoading } = useQuery({
    queryKey: ["project", id],
    queryFn: async () => (await api.get(`/projects/${id}`)).data.project,
  });

  if (isLoading || !project) {
    return <div className="grid place-items-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const c = PROJECT_STATUS_COLOR[project.status];

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="border-b border-border bg-[hsl(var(--sidebar))] px-5 sm:px-8 pt-6 pb-0">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wide"
                  style={{ color: c, backgroundColor: `${c}22`, border: `1px solid ${c}55` }}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c }} />{project.status}
                </span>
                <span className="label-caps">{project.currency}</span>
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight truncate" data-testid="project-title">{project.name}</h1>
            </div>
            <div className="flex gap-px bg-border border border-border rounded overflow-hidden shrink-0">
              <div className="bg-[hsl(var(--sidebar))] px-4 py-2.5">
                <div className="label-caps flex items-center gap-1.5"><Wallet className="h-3 w-3" /> Budget</div>
                <div className="font-data font-bold text-lg">{moneyExact(project.budget, project.currency)}</div>
              </div>
              <div className="bg-[hsl(var(--sidebar))] px-4 py-2.5">
                <div className="label-caps flex items-center gap-1.5"><CalendarDays className="h-3 w-3" /> Timeline</div>
                <div className="font-data font-bold text-sm mt-1">{project.start_date} → {project.end_date}</div>
              </div>
            </div>
          </div>

          <Tabs defaultValue="dashboard" className="w-full">
            <TabsList className="bg-transparent h-auto p-0 gap-1 flex-wrap justify-start">
              {[
                ["dashboard", "Dashboard"],
                ["tasks", "Task Board"],
                ["blueprints", "Blueprints"],
                ["channels", "Channels"],
                ["members", "Members"],
              ].map(([v, label]) => (
                <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                  className="h-11 px-5 rounded-none rounded-t-md border border-transparent data-[state=active]:border-border data-[state=active]:border-b-0 data-[state=active]:bg-background data-[state=active]:text-primary font-semibold text-muted-foreground -mb-px">
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="bg-background border-t border-border -mx-5 sm:-mx-8 px-5 sm:px-8 min-h-[70vh]">
              <div className="max-w-[1600px] mx-auto py-6">
                <TabsContent value="dashboard" className="mt-0"><DashboardTab projectId={id} currency={project.currency} /></TabsContent>
                <TabsContent value="tasks" className="mt-0"><TasksTab projectId={id} currency={project.currency} /></TabsContent>
                <TabsContent value="blueprints" className="mt-0"><BlueprintsTab projectId={id} /></TabsContent>
                <TabsContent value="channels" className="mt-0"><ChannelsTab projectId={id} /></TabsContent>
                <TabsContent value="members" className="mt-0"><MembersTab projectId={id} /></TabsContent>
              </div>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
